import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["status"];
import { useEffect, useRef, useState } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { Status } from './asyncState';
import useUpdatingRef from './useUpdatingRef';
const usePromiseEffect = (promise, {
  enabledFn = () => true,
  onSuccess = emptyFunction
} = {}) => {
  const [promiseState, setPromiseState] = useState({
    status: Status.UNINITIALIZED,
    data: undefined,
    error: undefined
  });
  const loading = promiseState.status === Status.PENDING;
  const onSuccessRef = useUpdatingRef(onSuccess);
  const enabled = enabledFn(Object.assign({}, promiseState, {
    loading
  }));
  const enabledRef = useUpdatingRef(enabled);
  const promiseRef = useRef(undefined);
  useEffect(() => {
    let expired = false;
    const fetch = () => {
      setPromiseState(state => Object.assign({}, state, {
        status: Status.PENDING
      }));
      promise().then(d => {
        if (expired) return;
        onSuccessRef.current(d);
        setPromiseState({
          error: undefined,
          data: d,
          status: Status.SUCCEEDED
        });
      }).catch(e => {
        if (expired) return;
        setPromiseState(state => Object.assign({}, state, {
          error: e,
          status: Status.ERRORED
        }));
      });
    };
    if (enabledRef.current) {
      fetch();
    }
    return () => {
      expired = true;
      setPromiseState(_ref => {
        let {
            status
          } = _ref,
          rest = _objectWithoutPropertiesLoose(_ref, _excluded);
        return Object.assign({}, rest, {
          status: status === Status.PENDING ? Status.CANCELED : status
        });
      });
    };
  }, [promise, enabledRef, onSuccessRef]);
  if (promiseRef.current !== promise && enabled) {
    promiseRef.current = promise;
    setPromiseState(state => Object.assign({}, state, {
      status: Status.PENDING
    }));
    // promise has changed! This runs immdidately (outside of an effect) to avoid returning a stale `status`
    return Object.assign({}, promiseState, {
      loading
    });
  }
  return Object.assign({}, promiseState, {
    loading
  });
};
export default usePromiseEffect;